import { isNativeFieldApp } from './installPrompt';

const SAVE_KEY = 'lgl-m-save-queue-v1';
const LAST_JOB_KEY = 'lgl-m-last-job';
const LAST_DISC_KEY = 'lgl-m-last-discipline';
const LAST_ROW_KEY = 'lgl-m-last-row';
export const STAY_ON_JOBS_KEY = 'lgl-m-stay-on-jobs';

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const loadSaveQueue = () => {
  const items = readJson(SAVE_KEY, []);
  return Array.isArray(items) ? items : [];
};

export const enqueueSave = (item) => {
  const queue = loadSaveQueue();
  const next = [
    ...queue.filter((entry) => !(
      String(entry.projectId) === String(item.projectId)
      && String(entry.disciplineId) === String(item.disciplineId)
      && String(entry.rowId) === String(item.rowId)
      && entry.columnId === item.columnId
    )),
    {
      ...item,
      queuedAt: Date.now(),
    },
  ];
  localStorage.setItem(SAVE_KEY, JSON.stringify(next));
  return next;
};

export const removeQueuedSave = (item) => {
  const next = loadSaveQueue().filter((entry) => !(
    String(entry.projectId) === String(item.projectId)
    && String(entry.disciplineId) === String(item.disciplineId)
    && String(entry.rowId) === String(item.rowId)
    && entry.columnId === item.columnId
  ));
  localStorage.setItem(SAVE_KEY, JSON.stringify(next));
  return next;
};

export const rememberLastJob = (project) => {
  if (!project?.id) return;
  localStorage.setItem(LAST_JOB_KEY, JSON.stringify({
    id: project.id,
    job_number: project.job_number || '',
    job_name: project.job_name || '',
  }));
};

export const readLastJob = () => readJson(LAST_JOB_KEY, null);

export const rememberLastDiscipline = (projectId, disciplineId) => {
  if (!projectId || !disciplineId) return;
  const map = readJson(LAST_DISC_KEY, {});
  map[String(projectId)] = disciplineId;
  localStorage.setItem(LAST_DISC_KEY, JSON.stringify(map));
};

export const readLastDiscipline = (projectId) => {
  if (!projectId) return null;
  const map = readJson(LAST_DISC_KEY, {});
  return map[String(projectId)] || null;
};

export const rememberLastRow = (projectId, disciplineId, rowId) => {
  if (!projectId || !disciplineId || rowId == null) return;
  const map = readJson(LAST_ROW_KEY, {});
  map[`${projectId}:${disciplineId}`] = String(rowId);
  localStorage.setItem(LAST_ROW_KEY, JSON.stringify(map));
};

export const readLastRow = (projectId, disciplineId) => {
  if (!projectId || !disciplineId) return null;
  const map = readJson(LAST_ROW_KEY, {});
  return map[`${projectId}:${disciplineId}`] || null;
};

export const saveErrorMessage = (err, fallback = 'Could not save') => {
  const status = err?.response?.status;
  if (status === 403) return 'Same as the website: you need progress-edit permission to save.';
  if (status === 401) {
    return isNativeFieldApp()
      ? 'Session expired — close LGL Portal and open it again.'
      : 'Session expired — open the website and sign in again.';
  }
  return err?.response?.data?.message || err?.message || fallback;
};

/** Drop a queued photo only on a hard 4xx that is not auth / permission. */
export const shouldDropFailedPhoto = (err) => {
  if (isNetworkError(err)) return false;
  const status = err?.response?.status;
  if (status === 401 || status === 403) return false;
  return status >= 400 && status < 500;
};

export const markStayOnJobs = () => {
  try { sessionStorage.setItem(STAY_ON_JOBS_KEY, '1'); } catch { /* private mode */ }
};

export const shouldStayOnJobs = () => {
  try { return sessionStorage.getItem(STAY_ON_JOBS_KEY) === '1'; } catch { return false; }
};

export const shouldAutoOpenOnlyJob = ({
  projectId,
  loading,
  query,
  stayOnJobs,
  projects,
}) => {
  if (projectId || loading || query || stayOnJobs) return null;
  if (!Array.isArray(projects) || projects.length !== 1) return null;
  return projects[0];
};

/**
 * Normal job tap always lands on the trackers list (job → trackers → ISOs → detail).
 * Continue may deep-link to the last walked tracker/ISO.
 */
export const pathAfterJobOpen = (jobId) => `/job/${jobId}`;

export const pathAfterJobContinue = (jobId, { lastDiscipline = null, lastRow = null } = {}) => {
  if (lastDiscipline && lastRow) return `/job/${jobId}/${lastDiscipline}/${lastRow}`;
  if (lastDiscipline) return `/job/${jobId}/${lastDiscipline}`;
  return `/job/${jobId}`;
};

export const pathAfterDisciplineOpen = (jobId, disciplineId) => `/job/${jobId}/${disciplineId}`;

export const pathAfterDisciplineContinue = (jobId, disciplineId, lastRow = null) => (
  lastRow ? `/job/${jobId}/${disciplineId}/${lastRow}` : `/job/${jobId}/${disciplineId}`
);

export const sortJobsForSuperintendent = (projects, lastJob) => {
  const list = [...projects];
  if (!lastJob?.id) return list;
  return list.sort((a, b) => {
    const aLast = String(a.id) === String(lastJob.id) ? 0 : 1;
    const bLast = String(b.id) === String(lastJob.id) ? 0 : 1;
    return aLast - bLast;
  });
};

export const isNetworkError = (error) => {
  if (!error) return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (!error.response) return true;
  return error.response.status >= 500;
};
